import React, { useState, useEffect, useRef } from 'react';
import { Play, Trophy, Hash, Layers, Zap } from 'lucide-react';
import { Fact } from '../../types';
import { motion } from 'framer-motion';
import CountdownAnimation from '../ui/CountdownAnimation';
import { FluencyPracticeSettings } from './FluencyPracticePage';

interface SelectionScreenProps {
  onStart: (settings: FluencyPracticeSettings) => void;
  facts: Fact[];
  errorMessage?: string | null;
  bestStreak: number | null;
  totalFacts: number;
  numSpeedLevels: number;
  startingSpeed: string;
}

const AUTO_START_SECONDS = 15;

const SelectionScreen: React.FC<SelectionScreenProps> = ({
  onStart,
  facts,
  errorMessage,
  bestStreak,
  totalFacts,
  numSpeedLevels,
  startingSpeed,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [autoStartTimer, setAutoStartTimer] = useState<NodeJS.Timeout | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(AUTO_START_SECONDS);
  const audioPlayedRef = useRef(false);

  // Auto-start timer
  useEffect(() => {
    if (!isLoading && facts.length > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            handleStart();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      setAutoStartTimer(timer);

      return () => {
        clearInterval(timer);
      };
    }
  }, [facts, isLoading]);

  // Voiceover audio
  useEffect(() => {
    if (!audioPlayedRef.current && facts.length > 0) {
      audioPlayedRef.current = true;
      const audioFile = bestStreak !== null
        ? '/assets/audio/fluencyPractice/selection_has_best.mp3'
        : '/assets/audio/fluencyPractice/selection_no_best.mp3';
      const audio = new Audio(audioFile);
      audio.play().catch(() => {});
    }
  }, [facts, bestStreak]);

  // Keyboard event listener for Enter key
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !isLoading && facts.length > 0) {
        handleStart();
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [facts, isLoading]);

  const handleStart = () => {
    if (facts.length === 0) return;

    if (autoStartTimer) {
      clearInterval(autoStartTimer);
      setAutoStartTimer(null);
    }

    setIsLoading(true);
    setShowCountdown(true);
  };

  const handleCountdownComplete = () => {
    try {
      onStart({
        facts,
        timerDuration: 6 // Overridden by parent component
      });
    } catch (error) {
      console.error('Failed to start Fluency practice:', error);
      setIsLoading(false);
      setShowCountdown(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-indigo-50 flex flex-col justify-center p-6">
      {showCountdown && (
        <CountdownAnimation onComplete={handleCountdownComplete} />
      )}

      <div className="max-w-md mx-auto space-y-6">
        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl font-bold text-center"
        >
          <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
            Fluency Practice
          </span>
        </motion.h1>

        {/* Best Streak Hero Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
          className="relative overflow-hidden rounded-2xl p-8 text-center bg-white border border-indigo-100 shadow-lg shadow-indigo-100/50"
        >
          {/* Decorative corner accents */}
          <div className="absolute top-0 right-0 w-28 h-28 bg-gradient-to-bl from-indigo-50 to-transparent rounded-bl-full" />
          <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-purple-50 to-transparent rounded-tr-full" />

          {bestStreak !== null ? (
            <div className="relative">
              <Trophy className="w-11 h-11 text-yellow-500 mx-auto mb-2" />
              <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-1">
                Best Streak
              </p>
              <span className="block text-7xl font-black bg-gradient-to-b from-indigo-500 to-purple-600 text-transparent bg-clip-text leading-tight">
                {bestStreak}
              </span>
              <p className="text-base font-bold text-indigo-500 mt-3">
                Can you beat it?
              </p>
            </div>
          ) : (
            <div className="relative">
              <Trophy className="w-11 h-11 text-indigo-200 mx-auto mb-2" />
              <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-1">
                Best Streak
              </p>
              <span className="block text-4xl font-black text-indigo-200 leading-tight">
                --
              </span>
              <p className="text-base font-bold text-indigo-500 mt-3">
                Set your first streak record!
              </p>
            </div>
          )}
        </motion.div>

        {/* Info Pills */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex items-center justify-center gap-2.5 flex-wrap"
        >
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white border border-indigo-100 text-indigo-600 text-sm font-medium shadow-sm">
            <Hash className="w-3.5 h-3.5" />
            {totalFacts} facts
          </span>
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white border border-indigo-100 text-indigo-600 text-sm font-medium shadow-sm">
            <Layers className="w-3.5 h-3.5" />
            {numSpeedLevels} speed levels
          </span>
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white border border-indigo-100 text-indigo-600 text-sm font-medium shadow-sm">
            <Zap className="w-3.5 h-3.5 text-yellow-500" />
            Starting at {startingSpeed}
          </span>
        </motion.div>

        {/* Auto-start bar */}
        {!isLoading && facts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="max-w-md mx-auto"
          >
            <div className="flex justify-between items-center text-sm text-gray-600 mb-2">
              <span>Auto-starting in</span>
              <span className="font-bold">{timeRemaining}s</span>
            </div>
            <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all duration-1000"
                style={{
                  width: `${(timeRemaining / AUTO_START_SECONDS) * 100}%`,
                }}
              />
            </div>
          </motion.div>
        )}

        {/* Start Button */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="pt-1"
        >
          <motion.button
            whileHover={{ scale: 1.03, boxShadow: '0 10px 40px -10px rgba(99, 102, 241, 0.5)' }}
            whileTap={{ scale: 0.97 }}
            onClick={handleStart}
            disabled={isLoading || facts.length === 0}
            className={`w-full px-8 py-5 bg-gradient-to-r from-indigo-500 to-purple-500
              text-white rounded-2xl font-bold text-xl shadow-lg shadow-indigo-200/50
              transition-all duration-300 flex items-center justify-center gap-3
              ${(isLoading || facts.length === 0) && 'opacity-50 cursor-not-allowed'}`}
          >
            <span>{isLoading ? 'Starting...' : 'Start Practice'}</span>
            {!isLoading && <Play className="w-6 h-6 fill-white" />}
          </motion.button>
        </motion.div>

        {/* Error display */}
        {errorMessage && facts.length === 0 && (
          <div className="text-red-500 font-medium text-center mt-2">
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectionScreen;
